/**
 * Checks a tenant theme stylesheet (feature plan 0054): CSS-variable declarations only, under any
 * selector. The editor shows these errors line by line before it sends anything; the backend runs the
 * same rules again as a backstop (`backend/src/Helper/TenantThemeCssValidator.php`).
 *
 * This is a 1:1 mirror of the PHP validator — same states, same allow-lists, same messages — and both
 * run the same fixture (`tests/fixtures/tenant-theme-css-cases.json`, a byte-identical copy of the
 * backend one). Change one, change both, and the fixture with them.
 *
 * It is a character tokenizer driven by allow-lists, not a CSS parser: wherever it is stricter than
 * the browser, that is a safe failure, never a hole. The size limit counts UTF-8 BYTES of the raw
 * text (TextEncoder), exactly like PHP's strlen().
 */

export interface TenantThemeCssError {
    line: number;
    message: string;
}

export const TENANT_THEME_MAX_BYTES = 65536;

const MAX_ERRORS = 50;

/** Registered via @property in spa.css (layout geometry); an off-syntax value breaks it silently. */
const LOCKED_VARIABLES = new Set([
    '--cell-width-multiplication',
    '--t3-column-section-3-size',
    '--t4-column-section-3-size',
]);

/** Value functions that fetch nothing (url() is checked separately). Compared lower-case. */
const ALLOWED_FUNCTIONS = new Set([
    'var', 'calc', 'min', 'max', 'clamp', 'rgb', 'rgba', 'hsl', 'hsla', 'hwb', 'lab', 'lch',
    'oklab', 'oklch', 'color-mix', 'light-dark', 'linear-gradient', 'radial-gradient',
    'conic-gradient', 'repeating-linear-gradient', 'repeating-radial-gradient',
    'repeating-conic-gradient', 'url',
]);

/** A data URL that is an image and Base64-encoded, anchored at both ends. Mirrors the PHP pattern. */
const DATA_IMAGE_BASE64 = /^data:image\/[a-z0-9.+-]+(;[a-z0-9-]+=[a-z0-9.-]+)*;base64,[A-Za-z0-9+/]*=*$/;

const PRELUDE_CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 \n\t(),:<>=.%/-';

const SELECTOR_CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 \n\t_.#:>+~*()[]=^$|,-';

class Validator {
    private src = '';
    private pos = 0;
    private len = 0;
    private line = 1;
    private errors: TenantThemeCssError[] = [];

    public validate(raw: string): TenantThemeCssError[] {
        if (new TextEncoder().encode(raw).length > TENANT_THEME_MAX_BYTES) {
            return [{ line: 1, message: 'Die Datei ist größer als 64 KiB.' }];
        }

        const src = raw.startsWith('﻿') ? raw.slice(1) : raw;
        this.src = src.replace(/\r\n|\r/g, '\n');
        this.pos = 0;
        this.len = this.src.length;
        this.line = 1;
        this.errors = [];

        this.parseStylesheet();

        // Stable sort by line, like PHP's usort on PHP 8 (stable).
        const sorted = this.errors
            .map((error, index) => ({ error, index }))
            .sort((a, b) => a.error.line - b.error.line || a.index - b.index)
            .map(entry => entry.error);

        return sorted.slice(0, MAX_ERRORS);
    }

    private parseStylesheet(): void {
        while (this.errors.length < MAX_ERRORS) {
            this.skipSpaceAndComments();
            if (this.eof()) {
                return;
            }

            const char = this.peek();
            if (char === '}') {
                this.error(this.line, 'Schließende Klammer „}“ ohne öffnende.');
                this.advance();
            } else if (char === '@') {
                this.parseMedia(false);
            } else {
                this.parseRule();
            }
        }
    }

    private parseMedia(inRule: boolean): void {
        const start = this.line;
        this.advance(); // '@'
        const name = this.readIdent();
        if (name.toLowerCase() !== 'media') {
            this.error(start, name === '' ? 'Unvollständige At-Regel.' : `Die At-Regel „@${name}“ ist nicht erlaubt.`);
            this.skipBlockOrStatement();
            return;
        }

        if (!this.readPrelude(start)) {
            return;
        }

        if (inRule) {
            this.parseDeclarationBlock(false);
            return;
        }

        // Top-level @media: selector rules only, one level.
        for (;;) {
            this.skipSpaceAndComments();
            if (this.eof()) {
                this.error(start, '„@media“-Block ist nicht geschlossen.');
                return;
            }
            const char = this.peek();
            if (char === '}') {
                this.advance();
                return;
            }
            if (char === '@') {
                this.error(this.line, 'At-Regeln dürfen nicht weiter verschachtelt werden.');
                this.advance();
                this.skipBlockOrStatement();
                continue;
            }
            this.parseRule();
        }
    }

    private readPrelude(start: number): boolean {
        let content = '';
        while (!this.eof()) {
            if (this.startsComment()) {
                if (!this.skipComment()) {
                    return false;
                }
                continue;
            }
            const char = this.peek();
            if (char === '{') {
                this.advance();
                if (content.trim() === '') {
                    this.error(start, 'Der „@media“-Ausdruck ist leer.');
                    this.skipToBlockEnd(1);
                    return false;
                }
                return true;
            }
            if (!PRELUDE_CHARS.includes(char)) {
                this.error(this.line, `Unerlaubtes Zeichen „${this.printable(char)}“ im „@media“-Ausdruck.`);
                this.skipBlockOrStatement();
                return false;
            }
            content += char;
            this.advance();
        }

        this.error(start, '„@media“ ohne Block.');
        return false;
    }

    private parseRule(): void {
        const start = this.line;
        let content = '';
        let depth = 0;
        while (!this.eof()) {
            if (this.startsComment()) {
                if (!this.skipComment()) {
                    return;
                }
                continue;
            }
            const char = this.peek();
            if (char === '{') {
                if (depth > 0) {
                    this.error(this.line, '„{“ innerhalb von Klammern im Selektor.');
                    this.skipBlockOrStatement();
                    return;
                }
                this.advance();
                if (content.trim() === '') {
                    this.error(start, 'Regel ohne Selektor.');
                    this.skipToBlockEnd(1);
                    return;
                }
                this.parseDeclarationBlock(true);
                return;
            }
            if (char === '"' || char === "'") {
                if (depth === 0) {
                    this.error(this.line, 'Zeichenketten sind im Selektor nur in „[…]“ erlaubt.');
                    this.skipBlockOrStatement();
                    return;
                }
                if (!this.readString()) {
                    this.skipBlockOrStatement();
                    return;
                }
                content += 's';
                continue;
            }
            if (!SELECTOR_CHARS.includes(char)) {
                this.error(this.line, `Unerlaubtes Zeichen „${this.printable(char)}“ im Selektor.`);
                this.skipBlockOrStatement();
                return;
            }
            if (char === '[' || char === '(') {
                depth++;
            } else if (char === ']' || char === ')') {
                depth--;
                if (depth < 0) {
                    this.error(this.line, `Klammer „${char}“ ohne öffnende im Selektor.`);
                    this.skipBlockOrStatement();
                    return;
                }
            }
            content += char;
            this.advance();
        }

        this.error(start, 'Selektor ohne Block.');
    }

    private parseDeclarationBlock(allowMedia: boolean): void {
        const start = this.line;
        while (this.errors.length < MAX_ERRORS) {
            this.skipSpaceAndComments();
            if (this.eof()) {
                this.error(start, 'Block ist nicht geschlossen.');
                return;
            }
            const char = this.peek();
            if (char === '}') {
                this.advance();
                return;
            }
            if (char === ';') {
                this.advance();
                continue;
            }
            if (char === '@') {
                if (allowMedia) {
                    this.parseMedia(true);
                } else {
                    this.error(this.line, 'At-Regeln dürfen nicht weiter verschachtelt werden.');
                    this.advance();
                    this.skipBlockOrStatement();
                }
                continue;
            }
            this.parseDeclaration();
        }
    }

    private parseDeclaration(): void {
        const start = this.line;
        const name = this.readIdent();
        if (name === '') {
            const char = this.peek();
            this.error(start, char === '{'
                ? 'Verschachtelte Selektoren sind nicht erlaubt.'
                : `Unerlaubtes Zeichen „${this.printable(char)}“ am Anfang einer Deklaration.`);
            this.skipDeclaration();
            return;
        }
        if (!name.startsWith('--') || name.length < 3) {
            this.error(start, `Nur CSS-Variablen (--name) sind erlaubt, nicht „${name}“.`);
            this.skipDeclaration();
            return;
        }
        if (LOCKED_VARIABLES.has(name)) {
            this.error(start, `„${name}“ ist eine Layout-Variable und nicht überschreibbar.`);
            this.skipDeclaration();
            return;
        }

        this.skipSpaceAndComments();
        if (this.peek() !== ':') {
            this.error(this.line, `Nach „${name}“ fehlt der Doppelpunkt.`);
            this.skipDeclaration();
            return;
        }
        this.advance();

        this.parseValue(start);
    }

    private parseValue(start: number): void {
        let depth = 0;
        let hasContent = false;
        while (!this.eof()) {
            if (this.startsComment()) {
                if (!this.skipComment()) {
                    return;
                }
                continue;
            }
            const char = this.peek();
            if (char === ' ' || char === '\t' || char === '\n') {
                this.advance();
                continue;
            }
            if (char === ';' || char === '}') {
                if (depth > 0) {
                    this.error(this.line, 'Klammer im Wert ist nicht geschlossen.');
                    this.skipDeclaration();
                    return;
                }
                if (!hasContent) {
                    this.error(start, 'Leerer Wert.');
                }
                return;
            }
            hasContent = true;
            if (char === '"' || char === "'") {
                if (!this.readString()) {
                    this.skipDeclaration();
                    return;
                }
                continue;
            }
            if (this.isIdentStart(char)) {
                const ident = this.readIdent();
                if (this.peek() === '(') {
                    const fn = ident.toLowerCase();
                    if (!ALLOWED_FUNCTIONS.has(fn)) {
                        this.error(this.line, `Die Funktion „${ident}()“ ist nicht erlaubt.`);
                        this.skipDeclaration();
                        return;
                    }
                    this.advance(); // '('
                    if (fn === 'url') {
                        if (!this.readUrl()) {
                            this.skipDeclaration();
                            return;
                        }
                        continue;
                    }
                    depth++;
                }
                continue;
            }
            if (this.isDigit(char) || (char === '.' && this.isDigit(this.peekNext())) || char === '#') {
                // Number, dimension or hex colour. A '.' belongs to it only when a digit follows (CSS
                // number grammar), and no '(' may follow the run: otherwise `.url(` or `1.url(` would
                // slip past readUrl() inside an allowed function (review 0054-01, R01-5).
                this.advance();
                while (!this.eof() && (this.isAlnum(this.peek()) || (this.peek() === '.' && this.isDigit(this.peekNext())))) {
                    this.advance();
                }
                if (this.peek() === '(') {
                    this.error(this.line, 'Klammer „(“ direkt nach einer Zahl oder Farbe im Wert.');
                    this.skipDeclaration();
                    return;
                }
                continue;
            }
            if (char === '(') {
                if (depth === 0) {
                    this.error(this.line, 'Klammer „(“ ohne Funktion im Wert.');
                    this.skipDeclaration();
                    return;
                }
                depth++;
                this.advance();
                continue;
            }
            if (char === ')') {
                if (depth === 0) {
                    this.error(this.line, 'Klammer „)“ ohne öffnende im Wert.');
                    this.skipDeclaration();
                    return;
                }
                depth--;
                this.advance();
                continue;
            }
            if (',/+*%'.includes(char)) {
                this.advance();
                continue;
            }

            this.error(this.line, `Unerlaubtes Zeichen „${this.printable(char)}“ im Wert.`);
            this.skipDeclaration();
            return;
        }

        if (depth > 0) {
            this.error(start, 'Klammer im Wert ist nicht geschlossen.');
        }
    }

    private readUrl(): boolean {
        const start = this.line;
        let target = '';
        const char = this.peek();
        if (char === '"' || char === "'") {
            const quote = char;
            this.advance();
            for (;;) {
                if (this.eof()) {
                    this.error(start, 'Zeichenkette in „url()“ ist nicht geschlossen.');
                    return false;
                }
                const c = this.peek();
                if (c === quote) {
                    this.advance();
                    break;
                }
                if (!this.isUrlChar(c)) {
                    this.error(this.line, `Unerlaubtes Zeichen „${this.printable(c)}“ in „url()“.`);
                    return false;
                }
                target += c;
                this.advance();
            }
            if (this.peek() !== ')') {
                this.error(this.line, '„url()“ ist nicht richtig geschlossen.');
                return false;
            }
            this.advance();
        } else {
            for (;;) {
                if (this.eof()) {
                    this.error(start, '„url()“ ist nicht geschlossen.');
                    return false;
                }
                const c = this.peek();
                if (c === ')') {
                    this.advance();
                    break;
                }
                if (!this.isUrlChar(c) || c === '"' || c === "'" || c === '(') {
                    this.error(this.line, `Unerlaubtes Zeichen „${this.printable(c)}“ in „url()“.`);
                    return false;
                }
                target += c;
                this.advance();
            }
        }

        if (!this.isAllowedUrlTarget(target)) {
            this.error(start, target.startsWith('data:')
                ? 'data-URL nur als „data:image/<typ>;base64,…“ (klein geschrieben, Base64).'
                : 'Bilder nur unter „/assets/…“, „../img/…“ oder als „data:image/<typ>;base64,…“.');
            return false;
        }

        return true;
    }

    /**
     * Mirrors PHP, which looks at single BYTES: there a non-ASCII character is several bytes
     * >= 0x80 and passes; here it is one code unit >= 0x80 and passes too.
     */
    private isUrlChar(char: string): boolean {
        const code = char.charCodeAt(0);
        return code > 0x20 && code !== 0x7f && char !== '\\' && char !== '%';
    }

    private isAllowedUrlTarget(target: string): boolean {
        if (target.startsWith('data:image/')) {
            // Base64 only, as the editor's hint promises: the media type (everything before the FIRST
            // comma) must end in ';base64', and the data must be Base64 — a ';base64,' further on in
            // the data does not count (review 0054-02, R02-1).
            return DATA_IMAGE_BASE64.test(target);
        }
        for (const prefix of ['/assets/', '../img/']) {
            if (target.startsWith(prefix)) {
                return !target.slice(prefix.length).includes('..');
            }
        }
        return false;
    }

    private readString(): boolean {
        const start = this.line;
        const quote = this.peek();
        this.advance();
        for (;;) {
            if (this.eof()) {
                this.error(start, 'Zeichenkette ist nicht geschlossen.');
                return false;
            }
            const char = this.peek();
            if (char === '\n') {
                this.error(start, 'Zeichenkette ist nicht geschlossen (Zeilenumbruch).');
                return false;
            }
            const code = char.charCodeAt(0);
            if ((code < 0x20 && char !== '\t') || code === 0x7f) {
                this.error(this.line, 'Steuerzeichen in einer Zeichenkette.');
                return false;
            }
            if (char === '\\') {
                const next = this.pos + 1 < this.len ? this.src[this.pos + 1] : '';
                if (next === '' || next === '\n') {
                    this.error(this.line, '„\\“ am Zeilen- oder Dateiende in einer Zeichenkette.');
                    return false;
                }
                this.advance();
                this.advance();
                continue;
            }
            this.advance();
            if (char === quote) {
                return true;
            }
        }
    }

    private readIdent(): string {
        let ident = '';
        while (!this.eof()) {
            const char = this.peek();
            if (!(this.isAlnum(char) || char === '-' || char === '_')) {
                break;
            }
            ident += char;
            this.advance();
        }
        return ident;
    }

    private isIdentStart(char: string): boolean {
        return this.isAlpha(char) || char === '-' || char === '_';
    }

    private isDigit(char: string): boolean {
        return char !== '' && char >= '0' && char <= '9';
    }

    private isAlpha(char: string): boolean {
        return char !== '' && ((char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z'));
    }

    private isAlnum(char: string): boolean {
        return this.isDigit(char) || this.isAlpha(char);
    }

    private skipSpaceAndComments(): void {
        while (!this.eof()) {
            const char = this.peek();
            if (char === ' ' || char === '\t' || char === '\n') {
                this.advance();
            } else if (this.startsComment()) {
                if (!this.skipComment()) {
                    return;
                }
            } else {
                return;
            }
        }
    }

    private startsComment(): boolean {
        return this.peek() === '/' && this.pos + 1 < this.len && this.src[this.pos + 1] === '*';
    }

    private skipComment(): boolean {
        const start = this.line;
        this.advance();
        this.advance();
        while (!this.eof()) {
            if (this.peek() === '*' && this.pos + 1 < this.len && this.src[this.pos + 1] === '/') {
                this.advance();
                this.advance();
                return true;
            }
            this.advance();
        }
        this.error(start, 'Kommentar ist nicht geschlossen.');
        return false;
    }

    private skipDeclaration(): void {
        let depth = 0;
        while (!this.eof()) {
            const char = this.peek();
            if (char === '{') {
                depth++;
            } else if (char === '}') {
                if (depth === 0) {
                    return;
                }
                depth--;
            } else if (char === ';' && depth === 0) {
                this.advance();
                return;
            }
            this.advance();
        }
    }

    private skipBlockOrStatement(): void {
        while (!this.eof()) {
            const char = this.peek();
            if (char === ';') {
                this.advance();
                return;
            }
            if (char === '{') {
                this.advance();
                this.skipToBlockEnd(1);
                return;
            }
            if (char === '}') {
                return;
            }
            this.advance();
        }
    }

    private skipToBlockEnd(depth: number): void {
        let open = depth;
        while (!this.eof() && open > 0) {
            const char = this.peek();
            if (char === '{') {
                open++;
            } else if (char === '}') {
                open--;
            }
            this.advance();
        }
    }

    private printable(char: string): string {
        const code = char.charCodeAt(0);
        if (code < 0x20 || code === 0x7f) {
            return `U+${code.toString(16).toUpperCase().padStart(4, '0')}`;
        }
        if (code >= 0x80) {
            return 'Nicht-ASCII-Zeichen';
        }
        return char;
    }

    private peek(): string {
        return this.src[this.pos] ?? '';
    }

    private peekNext(): string {
        return this.src[this.pos + 1] ?? '';
    }

    private advance(): void {
        if (this.pos < this.len && this.src[this.pos] === '\n') {
            this.line++;
        }
        this.pos++;
    }

    private eof(): boolean {
        return this.pos >= this.len;
    }

    private error(line: number, message: string): void {
        this.errors.push({ line, message });
    }
}

export class TenantThemeCssHelper {

    /** All errors of the stylesheet, sorted by line, at most 50; empty = valid. */
    public static validate(raw: string): TenantThemeCssError[] {
        return new Validator().validate(raw);
    }

    /**
     * The `[start, end)` character range of a 1-based line, for `setSelectionRange` when an error
     * entry jumps to its line. A line past the end clamps to the end of the text.
     */
    public static lineRange(text: string, line: number): [number, number] {
        let start = 0;
        for (let current = 1; current < line; current++) {
            const next = text.indexOf('\n', start);
            if (next === -1) {
                return [text.length, text.length];
            }
            start = next + 1;
        }
        const end = text.indexOf('\n', start);
        return [start, end === -1 ? text.length : end];
    }
}
